import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
} from '../../components/ui';
import { getBrand } from '../../constants/brands';
import { useAcceptWorkerInviteMutation } from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { serviceErrorMessage } from './serviceErrors';
import { inviteTokenFrom } from './workerInvite';

/**
 * `/accept-worker-invite?token=…` (spec 019 W8, D14b): the invitee signs in
 * or signs up first, then the invite is accepted once. Their jobs show in the
 * app; the web job page arrives with W19.
 */
export default function AcceptWorkerInvitePage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSnapshot(authStore);
  const token = inviteTokenFrom(search);
  const [accept, { loading, data, error }] = useAcceptWorkerInviteMutation();
  const tried = useRef(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!token || !isAuthenticated || tried.current) return;
    tried.current = true;
    accept({ variables: { token } }).catch(() => undefined);
  }, [token, isAuthenticated, accept, retry]);

  const brand = getBrand().name;

  let body;
  if (!token) {
    body = (
      <Alert tone="error" title="This link is not complete">
        Ask the Provider to send the invite link again.
      </Alert>
    );
  } else if (!isAuthenticated) {
    body = (
      <Stack $gap={4}>
        <Alert tone="info" title="Sign in to accept">
          Sign in, or create an account with the email or phone the invite was sent to. Then this
          page accepts the invite.
        </Alert>
        <div>
          <Button
            type="button"
            text="Sign in"
            onClick={() => modalActions.openSignInModal(() => undefined)}
            dataTestId="invite-sign-in"
          />
        </div>
      </Stack>
    );
  } else if (loading || (!data && !error)) {
    body = <LoadingBlock label="Accepting the invite" />;
  } else if (error) {
    body = (
      <Stack $gap={4}>
        <Alert tone="error" title="Could not accept the invite">
          {serviceErrorMessage(
            error,
            'The invite may have expired. Ask the Provider for a new one.',
          )}
        </Alert>
        <div>
          <Button
            type="button"
            theme="secondary"
            text="Try again"
            onClick={() => {
              tried.current = false;
              setRetry(n => n + 1);
            }}
          />
        </div>
      </Stack>
    );
  } else {
    body = (
      <Stack $gap={4}>
        <Alert tone="success" title="You're in" data-testid="invite-accepted">
          You now sign in as a worker on {brand}. Your jobs, with the address, checklist and photos,
          show in the {brand} app and on the web.
        </Alert>
        <div>
          <Button type="button" text="See your jobs" onClick={() => navigate('/services/worker')} />
        </div>
      </Stack>
    );
  }

  return (
    <PageContainer width="sm" canvas data-testid="accept-worker-invite">
      <PageHeader eyebrow={brand} title="Worker invite" />
      <Card>
        <CardBody>{body}</CardBody>
      </Card>
    </PageContainer>
  );
}
